import cn from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import Layout from 'components/Layout/Layout';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store';
import ProductItem from 'components/HighlightsPage/ProductItem/ProductItem';
import getIsLoggedIn from 'store/app/selector/getIsLoggedIn';
import fetchHighlightsOperation from 'store/favourite/operation/fetchHighlightsOperation';
import NoHighlights from 'components/HighlightsPage/NoHighlights/NoHighlights';
import getHighlights from 'store/favourite/selector/getHighlights';
import { OrderItemWithCustomerOrderId } from 'store/favourite/types';
import toSearchResultProductFromOrders from 'utils/toSearchResultProductFromOrders';
import ItemsLoadingSpinner from 'components/HighlightsPage/ItemsLoadingSpinner/ItemsLoadingSpinner';
import getLocaleSelector from 'store/app/selector/getLocaleSelector';
import isMobile from 'store/app/selector/isMobile';
import BringBackMemories from 'components/HighlightsPage/BringBackMemories/BringBackMemories';
import autoReorderOperation from 'store/favourite/operation/autoReorderOperation';
import DropdownSelectCompact from 'components/Common/DropdownSelectCompact/DropdownSelectCompact';
import getAddingProductToBasket from 'store/app/selector/getAddingProductToBasket';
import AddingProductSpinner from 'components/HighlightsPage/AddingProductSpinner/AddingProductSpinner';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import gtmSendFunnelStepEvent, { FUNNEL_STEPS } from 'utils/gtm/gtmSendFunnelStepEvent';
import OpButton from 'components/Common/op-ui-wrappers/OpButton/OpButton';
import styles from './Highlights.module.scss';

const DEFAULT_PRODUCT_TIER = 'all';
const SHOW_MORE_LIMIT = 24;

const lowerCaseAndIgnoreSpace = (s: string) => s.toLowerCase().replaceAll(' ', '');

const getProductYear = (timestamp: number) => {
  const date = new Date(timestamp * 1000);
  return date.getFullYear();
};

const HighlightsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProductTier, setSelectedProductTier] = useState<string>(DEFAULT_PRODUCT_TIER);
  const [isProductTierValidated, setIsProductTierValidated] = useState(false);
  const [showMoreLimit, setShowMoreLimit] = useState(SHOW_MORE_LIMIT);
  const locale = useSelector(getLocaleSelector);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isMobileDevice = useSelector(isMobile);
  const isAddingProductToBasket = useSelector(getAddingProductToBasket);

  const highlights = useSelector(getHighlights);
  const items = useMemo(
    () =>
      highlights
        .flatMap((order) =>
          order.items.map((item) => ({ ...item, customerOrderId: order.orderId } as OrderItemWithCustomerOrderId)),
        )
        .sort((a, b) => b.timestamp - a.timestamp),
    [highlights],
  );

  const productTiers = useMemo(
    () =>
      Array.from(new Set(items.map((item) => `${item.productTierLocalized}|${item.productTier}`)))
        .map((productTierPair) => {
          const [title, value] = productTierPair.split('|');
          return {
            title,
            value: lowerCaseAndIgnoreSpace(value),
          };
        })
        .filter((productTier) => productTier.title),
    [items],
  );

  const showNoHighlights = !isLoading && items.length === 0;

  const loadHighlights = async () => {
    await dispatch(fetchHighlightsOperation());
    setIsLoading(false);
  };

  const navigateToLoginPage = () => {
    const queryParams = new URLSearchParams(document.location.search);
    const orderItemId = Number(queryParams.get('orderItemId'));
    const productTier = queryParams.get('productTier');
    const paramsToPreserve = [];
    if (orderItemId) {
      paramsToPreserve.push(`orderItemId=${orderItemId}`);
    }
    if (productTier) {
      paramsToPreserve.push(`productTier=${productTier}`);
    }
    window.location.href = `/login?memoryLane=yes&redirectAfter=/highlights${
      paramsToPreserve.length ? `|${paramsToPreserve.join('||')}` : ''
    }`;
  };

  const setDefaultProductTier = () => {
    const queryParams = new URLSearchParams(document.location.search);
    const productTier = queryParams.get('productTier');
    if (productTier) {
      setSelectedProductTier(lowerCaseAndIgnoreSpace(productTier));
    } else {
      setSelectedProductTier(DEFAULT_PRODUCT_TIER);
    }
  };

  useEffect(() => {
    gtmSendFunnelStepEvent(FUNNEL_STEPS.memoryLane);
    if (!isLoggedIn) {
      navigateToLoginPage();
    } else {
      setDefaultProductTier();
      loadHighlights().then(() => {
        dispatch(autoReorderOperation());
      });
    }
  }, []);

  const mapProductTiers = () => {
    return [
      { title: t('txt_all_products'), value: DEFAULT_PRODUCT_TIER },
      ...productTiers.map((productTier) => ({
        title: productTier.title,
        value: lowerCaseAndIgnoreSpace(productTier.value),
      })),
    ];
  };

  const selectProductTier = (value: string) => {
    setSelectedProductTier(value);
    setShowMoreLimit(SHOW_MORE_LIMIT);
  };

  const itemsToShow = useMemo(
    () =>
      selectedProductTier === DEFAULT_PRODUCT_TIER
        ? items
        : items.filter((item) => lowerCaseAndIgnoreSpace(item.productTier) === selectedProductTier),
    [items, selectedProductTier],
  );

  const showFilterBy = productTiers.length > 1 && items.length > 4;

  const renderFilterBy = () =>
    showFilterBy ? (
      <div className={styles.productTypeSelector}>
        <DropdownSelectCompact
          label={t('txt_filter_by')}
          options={mapProductTiers()}
          selected={selectedProductTier}
          onChange={selectProductTier}
        />
      </div>
    ) : null;

  if (productTiers.length > 0 && !isProductTierValidated) {
    const isValid = productTiers.find((productTier) => productTier.value === selectedProductTier);
    if (!isValid) {
      setSelectedProductTier(DEFAULT_PRODUCT_TIER);
    }
    setIsProductTierValidated(true);
  }

  let currentYear = 0;

  const renderYearSplitter = (timestamp: number) => {
    if (currentYear !== getProductYear(timestamp)) {
      currentYear = getProductYear(timestamp);
      return (
        <div className={styles.yearSplitter}>
          <OpText variant="headline-medium-bold" color="neutral-40">
            {currentYear}
          </OpText>
          <div />
        </div>
      );
    }
    return null;
  };

  const showMoreClick = () => {
    setShowMoreLimit((v) => v + SHOW_MORE_LIMIT);
  };

  const renderShowMore = () => {
    if (itemsToShow.length < showMoreLimit) return null;
    return (
      <div className={styles.showMore}>
        <OpButton variant="secondary" size="small" onClick={showMoreClick}>
          {t('txt_show_more')}
        </OpButton>
      </div>
    );
  };

  return (
    <Layout className={cn({ [styles.blockInteractions]: isAddingProductToBasket })}>
      {isAddingProductToBasket && <AddingProductSpinner />}

      {!showNoHighlights && (
        <div className={styles.columns}>
          <BringBackMemories description="txt_bring_memories_back_description" />
          {!isMobileDevice && renderFilterBy()}
        </div>
      )}

      {isLoading && <ItemsLoadingSpinner />}

      {showNoHighlights && <NoHighlights />}

      {!showNoHighlights && (
        <>
          {isMobileDevice && renderFilterBy()}
          <div className={styles.highlightsList}>
            {itemsToShow.slice(0, showMoreLimit).map((product, index) => (
              <React.Fragment key={product.orderItemId}>
                {renderYearSplitter(product.timestamp)}
                <div className={styles.productItem}>
                  <ProductItem product={toSearchResultProductFromOrders(product, locale)} index={1 + index} />
                </div>
              </React.Fragment>
            ))}
            {renderShowMore()}
          </div>
        </>
      )}
    </Layout>
  );
};

export default HighlightsPage;
